import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import MainContainer from "../../../components/container/MainContainer";
import { useNotification } from "../../../components/notification/NotificationContainer";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect } from "react";
import {
  addProposalIssues,
  getProject,
  unsetSelectedProject,
} from "../../../features/projectsSlice";
import Loading from "../../../components/loading/Loading";
import InputField from "../../../components/form/InputField";
import PageTitle from "../../../components/titles/PageTitle";
import EditorField from "../../../components/form/EditorField";
import { addProposalIssuesSchema } from "../../../schemas";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";

const ProposalIssues = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useSelector((state) => state.projects.selected);
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const addProposalLoad = useSelector(
    (state) => state.projects.load.addProposalIssues
  );
  const addNotification = useNotification();
  const {
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      monitoring_supervision: "",
      issues: "",
      program_approach: "",
      sustainability_plan: "",
    },
    resolver: zodResolver(addProposalIssuesSchema),
  });

  const onSubmit = async (data, e) => {
    e.preventDefault();
    try {
      const res = await dispatch(addProposalIssues(data));
      if (addProposalIssues.fulfilled.match(res)) {
        addNotification("Successfully saved!", "success");
        return navigate("/proposals/budget/" + id);
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "error");
    }
  };

  useLayoutEffect(() => {
    dispatch(getProject(id));

    return () => {
      dispatch(unsetSelectedProject());
    };
  }, []);

  useEffect(() => {
    if (project.id) {
      reset({
        sustainability_plan: project.sustainability_plan || "",
        program_approach: project.program_approach || "",
        issues: project.issues || "",
        stakeholder_target_group: project.stakeholder_target_group || "",
        male_beneficiaries: project.male_beneficiaries,
        female_beneficiaries: project.female_beneficiaries,
        monitoring_supervision: project.monitoring_supervision,
      });
    }
  }, [project]);

  if (
    (id && project.id && project.stage.step_number < 8) ||
    (id && project.id && project.stage.step_number > 8)
  ) {
    return navigate("/requests/my-requests");
  }

  if (projectLoad === loadStatus.loading) {
    return <Loading />;
  }

  if (projectLoad === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-5xl mx-auto">
        <PageTitle title="Planning  - Proposal Issues" />
        {project && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            method="post"
            encType="multipart/form-data"
          >
            <div className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto">
              <h5 className="text-text-1/80 ">Proposal Details</h5>
            </div>
            <div className="text-text-1/60  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content">
              <EditorField
                register={register}
                name="issues"
                label="How this project helps address the issues of the GC26 & DC27"
                setValue={setValue}
                errors={errors.issues}
                defaultValue={project.issues || ""}
              />

              <div className="sm:flex justify-center items-center gap-10">
                <InputField
                  name="stakeholder_target_group"
                  register={register}
                  label="Stakeholder Target Group"
                  type="text"
                  errors={errors.stakeholder_target_group || ""}
                />
              </div>
              <div className="sm:flex justify-center items-center gap-10">
                <InputField
                  name="male_beneficiaries"
                  register={register}
                  label="Male Beneficaries"
                  type="number"
                  defaultValue={project.male_beneficiaries}
                  errors={errors.male_beneficiaries}
                />
                <InputField
                  name="female_beneficiaries"
                  register={register}
                  label="Female Beneficiaries"
                  type="number"
                  defaultValue={project.female_beneficiaries}
                  errors={errors.female_beneficiaries}
                />
              </div>

              <InputField
                register={register}
                name="id"
                type="hidden"
                defaultValue={id}
              />
              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/proposals/details/" + project.id);
                  }}
                  className="cancel-rss-btn"
                  disabled={addProposalLoad}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={addProposalLoad}
                  className="add-rss-btn"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </MainContainer>
  );
};

export default ProposalIssues;
